import type { WebCategory } from '../category/WebCategory';

/**
 * トピックスAPIのカテゴリの定義
 */
export const TopicsCategory = {
  // 主要
  MAJOR: 'main',
  // 国内
  DOMESTIC: 'domestic',
  // 国際
  WORLD: 'world',
  // 経済
  ECONOMY: 'economy',
  // エンタメ
  ENTERTAINMENT: 'entertainment',
  // スポーツ
  SPORTS: 'sports',
  // IT
  COMPUTER: 'computer',
  // 科学
  SCIENCE: 'science',
  // 地域
  LOCAL: 'local'
} as const;

export type TopicsCategory = (typeof TopicsCategory)[keyof typeof TopicsCategory];

/**
 * トピックスAPIカテゴリの日本語定義
 */
const TopicsCategoryJapanese = {
  [TopicsCategory.MAJOR]: '主要',
  [TopicsCategory.DOMESTIC]: '国内',
  [TopicsCategory.WORLD]: '国際',
  [TopicsCategory.ECONOMY]: '経済',
  [TopicsCategory.ENTERTAINMENT]: 'エンタメ',
  [TopicsCategory.SPORTS]: 'スポーツ',
  [TopicsCategory.COMPUTER]: 'IT',
  [TopicsCategory.SCIENCE]: '科学',
  [TopicsCategory.LOCAL]: '地域'
} as const satisfies Record<TopicsCategory, string>;

type TopicsCategoryJapanese = (typeof TopicsCategoryJapanese)[TopicsCategory];

const topicsCategoryByWebCategory = {
  major: TopicsCategory.MAJOR,
  'top-picks': TopicsCategory.MAJOR,
  domestic: TopicsCategory.DOMESTIC,
  world: TopicsCategory.WORLD,
  business: TopicsCategory.ECONOMY,
  entertainment: TopicsCategory.ENTERTAINMENT,
  sports: TopicsCategory.SPORTS,
  it: TopicsCategory.COMPUTER,
  science: TopicsCategory.SCIENCE,
  local: TopicsCategory.LOCAL
} as const satisfies Partial<Record<WebCategory, TopicsCategory>>;

const topicsCategoryValues = new Set<string>(Object.values(TopicsCategory));

const isTopicsCategory = (category: string): category is TopicsCategory => topicsCategoryValues.has(category);

const hasTopicsCategoryByWebCategory = (category: WebCategory): category is keyof typeof topicsCategoryByWebCategory =>
  Object.hasOwn(topicsCategoryByWebCategory, category);

/**
 * Webのカテゴリに紐づくトピックスのカテゴリを返す
 *
 * トピックスに存在しないcategoryを渡された場合は、undefinedを返す
 *
 * @param category
 */
export function fromWebCategory(category?: WebCategory): TopicsCategory | undefined {
  if (!category || !hasTopicsCategoryByWebCategory(category)) {
    return undefined;
  }
  return topicsCategoryByWebCategory[category];
}

/**
 * stringをTopicsCategoryに変換する
 * 存在しないcategoryを渡された場合は、undefinedを返す
 *
 * @param category
 */
export function fromString(category: string): TopicsCategory | undefined {
  return isTopicsCategory(category) ? category : undefined;
}

/**
 * TopicsCategoryを日本語に変換する
 *
 * @param topicsCategory
 */
export function toJapanese(topicsCategory: TopicsCategory): TopicsCategoryJapanese {
  return TopicsCategoryJapanese[topicsCategory];
}
